import React from 'react';
import { Phone, MapPin, Clock, Heart, ChevronDown, ChevronUp, Star } from 'lucide-react';
import { useFavoriteTaxiStands } from '../hooks/useFavoriteTaxiStands';
import { calculateWaitTime } from '../utils/taxiUtils';

interface TaxiPlace {
  name: string;
  address: string;
  phone?: string;
  distance?: string;
  duration?: string;
  rating?: number;
  totalReviews?: number;
}

interface NearbyTaxiListProps {
  places: TaxiPlace[];
  loading: boolean;
  error?: string;
  neighborhoodName?: string;
  showTitle?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

export default function NearbyTaxiList({ 
  places, 
  loading, 
  error, 
  neighborhoodName,
  showTitle = true,
  isExpanded,
  onToggle
}: NearbyTaxiListProps) {
  const { addFavorite, removeFavorite, isFavorite } = useFavoriteTaxiStands();

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse bg-yellow-200/50 rounded-lg overflow-hidden">
            <div className="p-3">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-yellow-500/30 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-yellow-500/30 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-yellow-500/30 rounded w-1/2"></div>
                </div>
              </div>
              <div className="h-8 bg-yellow-500/30 rounded-lg mt-3"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  if (places.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-black/60">Bu bölgede taksi durağı bulunamadı.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {showTitle && (
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-black">
            Yakındaki Taksi Durakları
          </h2>
          <button
            onClick={onToggle}
            className="flex items-center gap-1 text-black/70 hover:text-black transition-colors"
          >
            {isExpanded ? (
              <>
                <span className="text-sm">Gizle</span>
                <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                <span className="text-sm">Göster</span>
                <ChevronDown className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      )}
      
      {isExpanded && (
        <div className="space-y-2">
          {places.map((place, index) => (
            <div
              key={index}
              className="bg-yellow-200/50 rounded-lg overflow-hidden"
            >
              <div className="p-3">
                <div className="flex gap-2">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-base font-black text-black">T</span>
                  </div>
                    
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-black font-medium text-sm leading-tight truncate w-36">{place.name}</h3>
                        <p className="text-xs text-black/60 truncate w-36">{place.address}</p>
                      </div>
                      <button
                        onClick={() => {
                          const taxiStand = {
                            id: index,
                            name: place.name,
                            address: place.address,
                            phone: place.phone || '',
                            rating: place.rating || 0,
                            available: 0,
                            hours: '7/24',
                            neighborhoodId: 0,
                            isVerified: false,
                            totalReviews: place.totalReviews || 0,
                            reviews: []
                          };
                          
                          if (isFavorite(index)) {
                            removeFavorite(index);
                          } else {
                            addFavorite(taxiStand);
                          }
                        }}
                      >
                        <Heart 
                          className={`w-5 h-5 ${
                            isFavorite(index) 
                              ? 'text-red-500 fill-red-500' 
                              : 'text-black/40 hover:text-red-500'
                          }`} 
                        />
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-black/60" />
                        <span className="text-xs text-black/70">{place.distance}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-black/60" />
                        <span className="text-xs text-black/70">
                          {calculateWaitTime(place.distance || '0 km')}
                        </span>
                      </div>
                      {place.rating && (
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-600 fill-current" />
                          <span className="text-xs font-medium text-black/70">
                            {place.rating.toFixed(1)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {place.phone && (
                  <a
                    href={`tel:${place.phone}`}
                    className="block w-full mt-2 bg-[#0e0d0d] hover:bg-[#1e1d1d] text-white font-medium rounded-lg transition-all duration-150 active:scale-[0.98] hover:shadow-sm"
                  >
                    <div className="px-3 py-2 flex items-center justify-center gap-2">
                      <Phone className="w-3.5 h-3.5" />
                      <span className="text-sm">Taksi Çağır</span>
                    </div>
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}